// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useMutation } from '@apollo/client';
import type { WeightUnit } from '@seedplanner/shared-utils';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { PageLoading } from '../components/common/loading';
import { useLoggedInState } from '../context/LoggedInContext';
import { type UserContextValue, useUserContext } from '../context/UserContext';
import { Box, Button, Card, Container, Grid, NoSsr, TextField, Typography } from '../lib/base';
import { CenteredView } from '../lib/layout';
import { Link } from '../lib/link';
import { SIGNIN_MUTATION } from '../queries/SignInQueries';
import * as routes from '../routes';
import type { LoginMutation, LoginMutationVariables } from '../types/__generated__/graphql';

export default function SignIn() {
  const { isLoggedIn, setIsLoggedIn } = useLoggedInState();
  const { setUserContext } = useUserContext();
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [login, { loading }] = useMutation<LoginMutation, LoginMutationVariables>(SIGNIN_MUTATION);

  const onChangeEmail = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEmail(event.target.value);
  };
  const onChangePassword = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPassword(event.target.value);
  };

  const submitDisabled = email === '' || password === '' || loading;
  const fieldsDisabled = loading;

  const onLoginSuccess = (res: UserContextValue) => {
    setEmail('');
    setPassword('');
    setIsLoggedIn(true);
    setUserContext(res);
    navigate(routes.DASHBOARD);
  };
  const onLoginFailure = (error: Error) => {
    enqueueSnackbar(error.message, { variant: 'error', autoHideDuration: 10000 });
  };
  const onClickSubmit = () => {
    if (submitDisabled) {
      return;
    }
    void login({
      variables: {
        email,
        password,
      },
      onCompleted: (data) => {
        if (data.login) {
          const dataIn = data.login;
          onLoginSuccess({
            timezone: dataIn.timezone,
            permissions: dataIn.permissions,
            darkModePreference: dataIn.darkModePreference as 'light' | 'dark' | 'system',
            preferredWeightUnit: dataIn.preferredWeightUnit as WeightUnit,
          });
        }
      },
      onError: (error) => {
        onLoginFailure(error);
      },
    });
  };

  return (
    <CenteredView>
      <Helmet>
        <title>{'Sign In'}</title>
        <meta name="description" content={'Sign in to Microgreen Manager.'} />
      </Helmet>
      <Container disableGutters component="div" maxWidth="xs">
        <Card
          sx={{
            marginTop: 6,
            marginBottom: 6,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: 2,
            // maxWidth: '444px',
            // marginLeft: 'auto',
            // marginRight: 'auto',
          }}
        >
          {isLoggedIn ? (
            <Typography sx={{ p: 2, textAlign: 'center' }} variant="h5">
              You are already logged in. If you wish to log into a different account, please log out first.
            </Typography>
          ) : (
            <>
              {/* TODO: should put Microgreen Manager icon here */}
              <Typography sx={{ p: 1, textAlign: 'center' }} variant="h4">
                Sign In to Microgreen Manager
              </Typography>
              <Box sx={{ mt: 3, width: '100%' }}>
                <NoSsr fallback={<PageLoading />}>
                  <Grid container spacing={2}>
                    <Grid xs={12}>
                      <TextField
                        aria-describedby="email-input"
                        id="email-input"
                        label="Email"
                        inputMode="email"
                        type="email"
                        required
                        value={email}
                        onChange={onChangeEmail}
                        disabled={fieldsDisabled}
                        onEnter={onClickSubmit}
                      />
                    </Grid>
                    <Grid xs={12}>
                      <TextField
                        aria-describedby="password-input"
                        id="password-input"
                        label="Password"
                        type="password"
                        required
                        value={password}
                        onChange={onChangePassword}
                        disabled={fieldsDisabled}
                        onEnter={onClickSubmit}
                      />
                    </Grid>
                  </Grid>
                  <Button
                    disabled={submitDisabled}
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                    onClick={onClickSubmit}
                  >
                    Sign In
                  </Button>
                </NoSsr>
                <Grid columnGap={1} rowGap={1} container justifyContent="center">
                  <Grid>
                    <Typography variant="body2">
                      Need to create an account?{' '}
                      <Link inlineText variant="body2" path={routes.SIGNUP} title="Sign up" />.
                    </Typography>
                  </Grid>
                  <Grid>
                    <Typography variant="body2">
                      Forgot your password?{' '}
                      <Link inlineText variant="body2" path={routes.FORGOT_PASSWORD} title="Reset your password" />.
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </Card>
      </Container>
    </CenteredView>
  );
}
